import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Settings, ArrowLeft, Plus, Trash2, AlertCircle,
  Cpu, HardDrive, Network, Lock, EyeOff,
} from 'lucide-react'
import { GlobalStandardsExplorer, type DefaultParents } from './GlobalStandardsExplorer.tsx'
import { usePermission } from '@/hooks/usePermission.ts'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface StandardItem {
  id: string
  type: 'group' | 'share' | 'gpo' | 'user' | 'ou' | string
  name_template: string
  description?: string
  config?: { driveLetter?: string; relativePath?: string; groupType?: string; parent?: string }
  sort_order: number
}

interface GpoParameter {
  key: string
  label: string
  type: 'int' | 'bool'
  default: number | boolean
  min?: number
  max?: number
  unit?: string
  description?: string
}

interface StandardGpo {
  key: string
  nameTemplate: string
  description: string
  linkOu: string
  enforced?: boolean
  parameters: GpoParameter[]
}

// ─── Section Component ────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, color, count }: {
  icon: React.ElementType
  title: string
  color: string
  count: number
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={cn('w-4 h-4', color)} />
      <h2 className="text-small font-semibold text-gray-900">{title}</h2>
      <Badge variant="neutral" size="sm">{count}</Badge>
    </div>
  )
}

function ItemRow({ item, onDelete, canEdit = true }: { item: StandardItem; onDelete: () => void; canEdit?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="flex-1 min-w-0">
        <span className="text-small text-gray-900 font-mono">{item.name_template}</span>
        {item.config?.groupType && (
          <Badge variant="accent" size="sm" className="ml-2 font-mono">
            {item.config.groupType}
          </Badge>
        )}
        {item.config?.driveLetter && (
          <Badge variant="warning" size="sm" className="ml-2 font-mono">
            {item.config.driveLetter}:
          </Badge>
        )}
        {item.config?.relativePath && (
          <span className="ml-1 text-caption text-gray-400 font-mono">
            → \{item.config.relativePath}
          </span>
        )}
        {item.description && (
          <p className="text-caption text-gray-400 mt-0.5">{item.description}</p>
        )}
      </div>
      {canEdit && (
        <IconButton
          icon={<Trash2 />}
          aria-label="Löschen"
          variant="danger"
          size="sm"
          onClick={onDelete}
        />
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function GlobalStandardsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { allowed: canEdit } = usePermission('infrastructure', 'update')

  // Default-Parents (Speicherorte pro Gruppen-Typ / User)
  const [defaultParents, setDefaultParents] = useState<DefaultParents>({})

  // Active tab
  const [activeTab, setActiveTab] = useState<'ous' | 'shares' | 'gpos'>('ous')

  // Shares form
  const [showAddShare, setShowAddShare] = useState(false)
  const [shrName, setShrName] = useState('')
  const [shrDrive, setShrDrive] = useState('')
  const [shrPath, setShrPath] = useState('')
  const [shrDesc, setShrDesc] = useState('')
  const [shrError, setShrError] = useState<string | null>(null)

  // ─── Queries ────────────────────────────────────────────────────────────────

  const { data: globalResponse } = useQuery<{ data: Record<string, unknown> }>({
    queryKey: ['it-global-standards'],
    queryFn: () => apiClient.get('/it-structures/global').then((r) => r.data),
  })

  useEffect(() => {
    if (!globalResponse) return
    const dp = globalResponse.data['defaultParents'] as DefaultParents | undefined
    if (dp && typeof dp === 'object') setDefaultParents(dp)
  }, [globalResponse])

  const { data: itemsData, isLoading } = useQuery<{ items: StandardItem[] }>({
    queryKey: ['it-standard-items'],
    queryFn: () => apiClient.get('/it-structures/global/items').then((r) => r.data),
  })
  const items = itemsData?.items ?? []
  const shares = items.filter((i) => i.type === 'share' || (i.config?.driveLetter != null && !['group', 'gpo', 'user', 'ou'].includes(i.type)))

  // Standard-GPOs sind fest im Code (Backend: apps/backend/src/config/standard-gpos.ts)
  const { data: gposData, isLoading: gposLoading } = useQuery<{ gpos: StandardGpo[] }>({
    queryKey: ['it-gpo-standards'],
    queryFn: () => apiClient.get('/it-structures/gpo-standards').then((r) => r.data),
  })
  const standardGpos = gposData?.gpos ?? []

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const addItem = useMutation({
    mutationFn: (payload: object) => apiClient.post('/it-structures/global/items', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['it-standard-items'] }),
  })

  const deleteItem = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/it-structures/global/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['it-standard-items'] }),
  })

  function cancelAddShare() {
    setShowAddShare(false)
    setShrName(''); setShrDrive(''); setShrPath(''); setShrDesc(''); setShrError(null)
  }

  function addShare() {
    if (!shrName.trim()) { setShrError('Share-Name ist erforderlich'); return }
    setShrError(null)
    addItem.mutate({
      type: 'share',
      name_template: shrName.trim(),
      description: shrDesc || undefined,
      config: {
        driveLetter: shrDrive.toUpperCase() || undefined,
        relativePath: shrPath || undefined,
      },
      sort_order: items.length,
    }, {
      onSuccess: () => { setShrName(''); setShrDrive(''); setShrPath(''); setShrDesc(''); setShowAddShare(false) },
      onError: (e: Error) => setShrError(e.message),
    })
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  const legacyItems = items.filter((i) => !['group', 'share', 'user', 'ou'].includes(i.type) && !i.config?.driveLetter)

  type TabId = 'ous' | 'shares' | 'gpos'
  const tabs: { id: TabId; label: string; icon: React.ElementType; color: string; count: number }[] = [
    { id: 'ous', label: 'OUs, Gruppen & Benutzer', icon: Network, color: 'text-green-500', count: items.filter((i) => ['ou', 'group', 'user'].includes(i.type)).length },
    { id: 'shares', label: 'Laufwerke & Freigaben', icon: HardDrive, color: 'text-orange-500', count: isLoading ? 0 : shares.length },
    { id: 'gpos', label: 'Standard-GPOs', icon: Cpu, color: 'text-rose-500', count: gposLoading ? 0 : standardGpos.length },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Settings className="w-7 h-7 text-brand-500" />
            Globale Standards
            {!canEdit && (
              <Badge variant="warning" size="sm" className="gap-1">
                <EyeOff className="w-3.5 h-3.5" /> Nur Lesen
              </Badge>
            )}
          </span>
        }
        description="Namenskonventionen und Pflicht-Elemente für alle Kunden"
        actions={
          <IconButton
            icon={<ArrowLeft />}
            aria-label="Zurück zur Übersicht"
            variant="ghost"
            onClick={() => navigate('/infrastruktur/strukturen')}
          />
        }
      />

      {!canEdit && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3 px-4 py-3 text-small text-amber-800">
            <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Du hast keine Berechtigung zum Bearbeiten der globalen Standards. Wende dich an einen Administrator.</span>
          </div>
        </Card>
      )}

      {/* Tab-Navigation */}
      <Card>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
          <TabsList className="px-2 pt-1">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <TabsTrigger key={tab.id} value={tab.id}>
                  <Icon className={cn('w-4 h-4', isActive ? tab.color : 'text-gray-400')} />
                  {tab.label}
                  <Badge variant={isActive ? 'brand' : 'neutral'} size="sm">
                    {tab.count}
                  </Badge>
                </TabsTrigger>
              )
            })}
          </TabsList>

          {/* ── OUs / Gruppen / User als Baum ──────────────────────────────────── */}
          <TabsContent value="ous" className="p-5 space-y-3">
            <p className="text-caption text-gray-500">
              Pflicht-OUs, AD-Gruppen und Standard-Accounts als Hierarchie.
              Rechtsklick auf den Domain-Root oder eine OU zum Anlegen;
              „Als Standard setzen für…" setzt eine OU als Default-Speicherort pro Typ.
            </p>
            <GlobalStandardsExplorer
              items={items}
              defaultParents={defaultParents}
              isLoading={isLoading}
              canEdit={canEdit}
            />
          </TabsContent>

          {/* ── Laufwerke & Freigaben ──────────────────────────────────────────── */}
          <TabsContent value="shares" className="pt-0">
            <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-caption text-gray-500">
                Standard-Netzfreigaben mit Laufwerksbuchstabe — der Serverpfad wird je Kunde im Server-Tab gesetzt
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {isLoading && <LoadingState size="sm" label="Lade…" />}
              {!isLoading && shares.length === 0 && (
                <EmptyState
                  size="sm"
                  icon={<HardDrive />}
                  title="Keine Standard-Freigaben"
                  description="Noch keine Standard-Freigaben definiert."
                />
              )}
              {shares.map((item) => (
                <ItemRow key={item.id} item={item} onDelete={() => deleteItem.mutate(item.id)} canEdit={canEdit} />
              ))}
            </div>
            {canEdit && (
              <div className="border-t border-dashed border-gray-300 p-4 bg-gray-50/50">
                {showAddShare ? (
                  <>
                    <p className="text-caption font-medium text-gray-600 mb-3">Neue Freigabe hinzufügen</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <Input
                        label="Freigabe-Name"
                        type="text"
                        value={shrName}
                        onChange={(e) => setShrName(e.target.value)}
                        placeholder="z.B. Administration$"
                        className="font-mono"
                      />
                      <Input
                        label="Laufwerksbuchstabe (Mapping)"
                        type="text"
                        maxLength={1}
                        value={shrDrive}
                        onChange={(e) => setShrDrive(e.target.value.toUpperCase())}
                        placeholder="z.B. M"
                        className="font-mono text-center"
                      />
                      <Input
                        label="Relativer Pfad auf Server"
                        type="text"
                        value={shrPath}
                        onChange={(e) => setShrPath(e.target.value)}
                        placeholder="z.B. Administration"
                        className="font-mono"
                      />
                      <Input
                        label="Beschreibung (optional)"
                        type="text"
                        value={shrDesc}
                        onChange={(e) => setShrDesc(e.target.value)}
                        placeholder="z.B. Administrations-Laufwerk"
                      />
                    </div>
                    {(shrName || shrDrive || shrPath) && (
                      <div className="mt-2 text-caption text-gray-400 font-mono bg-gray-100 rounded px-2 py-1.5">
                        Freigabe: <span className="text-gray-700">\\SERVER\{shrName || '…'}</span>
                        {shrDrive && <> → <span className="text-gray-700">{shrDrive}:</span></>}
                        {shrPath && <> (Lokaler Pfad: <span className="text-gray-700">LAUFWERK:\{shrPath}</span>)</>}
                      </div>
                    )}
                    {shrError && (
                      <p className="text-caption text-red-600 flex items-center gap-1 mt-2">
                        <AlertCircle className="w-3.5 h-3.5" />{shrError}
                      </p>
                    )}
                    <div className="mt-3 flex items-center gap-3">
                      <Button
                        variant="primary"
                        size="sm"
                        leadingIcon={<Plus className="w-4 h-4" />}
                        onClick={addShare}
                        disabled={addItem.isPending}
                      >
                        Hinzufügen
                      </Button>
                      <Button variant="link" size="sm" onClick={cancelAddShare}>
                        Abbrechen
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<Plus className="w-4 h-4" />}
                    onClick={() => setShowAddShare(true)}
                  >
                    Freigabe hinzufügen
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          {/* ── GPO-Templates (read-only, fest im Code) ────────────────────────── */}
          <TabsContent value="gpos" className="pt-0">
            <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-caption text-gray-500">
                Fest definierte Group Policy Objects die auf jedem Kunden-AD ausgerollt werden.
                Werte (z.B. Passwortlänge, Lock-Timeout) werden pro Kunde in der Kundenstruktur gesetzt.
              </p>
            </div>
            <div className="bg-blue-50/50 border-b border-blue-100 px-4 py-2 text-caption text-blue-700 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" />
              Struktur festgelegt im Code. Neue GPOs = Eintrag in&nbsp;
              <span className="font-mono">apps/backend/src/config/standard-gpos.ts</span>.
            </div>
            <div className="divide-y divide-gray-100">
              {gposLoading && <LoadingState size="sm" label="Lade…" />}
              {!gposLoading && standardGpos.length === 0 && (
                <EmptyState
                  size="sm"
                  icon={<Cpu />}
                  title="Keine Standard-GPOs"
                  description="Keine Standard-GPOs definiert."
                />
              )}
              {standardGpos.map((gpo) => (
                <div key={gpo.key} className="px-4 py-3 space-y-2">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-small font-mono text-gray-900">{gpo.nameTemplate}</span>
                    <Badge variant="neutral" size="sm" className="font-mono">
                      → {gpo.linkOu}
                    </Badge>
                    {gpo.enforced && (
                      <Badge variant="warning" size="sm">enforced</Badge>
                    )}
                  </div>
                  <p className="text-caption text-gray-500">{gpo.description}</p>
                  {gpo.parameters.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {gpo.parameters.map((p) => (
                        <Badge
                          key={p.key}
                          variant="neutral"
                          size="sm"
                          className="font-mono"
                          title={p.description}
                        >
                          {p.label}: <span className="text-gray-900">{String(p.default)}</span>
                          {p.unit ? ` ${p.unit}` : ''}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </Card>

      {/* ── Veraltete Eintraege (immer ausserhalb der Tabs, nur wenn vorhanden) ── */}
      {legacyItems.length > 0 && (
        <Card>
          <Card.Header className="bg-gray-50 flex-col items-start">
            <SectionHeader icon={Network} title="Veraltete Einträge" color="text-gray-400" count={legacyItems.length} />
            <p className="text-caption text-gray-500 mt-0.5">
              Früher hier manuell angelegte GPO-Templates. Können gelöscht werden — Standard-GPOs sind jetzt fest im Code.
            </p>
          </Card.Header>
          <div className="divide-y divide-gray-100">
            {legacyItems.map((item) => (
              <ItemRow key={item.id} item={item} onDelete={() => deleteItem.mutate(item.id)} canEdit={canEdit} />
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
